export interface JobFrontmatter {
    title: string;
    company: string;
    location: string;
    salary: string;
    posted: string;
    deadline: string;
    type: string;
    experience: string;
    tags: string;
  }
  
  export interface Job extends JobFrontmatter {
    id: string;
    body: string;
  }
  
  /**
   * Simple frontmatter parser - extracts YAML frontmatter from markdown
   * Format: ---\nkey: value\n---\ncontent
   */
  function parseFrontmatter(markdown: string): {
    frontmatter: Record<string, string>;
    content: string;
  } {
    const lines = markdown.split('\n');
    
    if (lines[0]?.trim() !== '---') {
      return { frontmatter: {}, content: markdown };
    }
  
    let endIndex = -1;
    for (let i = 1; i < lines.length; i++) {
      if (lines[i]?.trim() === '---') {
        endIndex = i;
        break;
      }
    }
  
    if (endIndex === -1) {
      return { frontmatter: {}, content: markdown };
    }
  
    const frontmatterLines = lines.slice(1, endIndex);
    const frontmatter: Record<string, string> = {};
  
    for (const line of frontmatterLines) {
      if (!line.trim()) continue;
      const [key, ...valueParts] = line.split(':');
      if (key) {
        frontmatter[key.trim().toLowerCase()] = valueParts.join(':').trim();
      }
    }
  
    const content = lines.slice(endIndex + 1).join('\n').trim();
  
    return { frontmatter, content };
  }
  
  /**
   * Convert markdown to HTML - simplified version
   * Handles: headings, bold, italic, lists, links, paragraphs
   */
  export function markdownToHtml(markdown: string): string {
    let html = markdown;
  
    // Headings
    html = html.replace(/^### (.*?)$/gm, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>');
    html = html.replace(/^## (.*?)$/gm, '<h2 class="text-xl font-bold mt-5 mb-3">$1</h2>');
    html = html.replace(/^# (.*?)$/gm, '<h1 class="text-2xl font-bold mt-6 mb-4">$1</h1>');
  
    // Bold and italic
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>');
    html = html.replace(/\*(.*?)\*/g, '<em class="italic">$1</em>');
  
    // Links
    html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-blue-600 hover:underline">$1</a>');
  
    // Unordered lists
    html = html.replace(/^\- (.*?)$/gm, '<li class="ml-4">$1</li>');
    html = html.replace(/(<li class="ml-4">.*?<\/li>)/s, '<ul class="list-disc mb-4">$1</ul>');
  
    // Paragraphs - wrap non-empty lines not in other tags
    const lines = html.split('\n');
    html = lines
      .map((line) => {
        const trimmed = line.trim();
        if (
          !trimmed ||
          trimmed.startsWith('<') ||
          trimmed.startsWith('</')
        ) {
          return line;
        }
        return `<p class="mb-3 leading-relaxed">${trimmed}</p>`;
      })
      .join('\n');
  
    return html;
  }
  
  /**
   * Load all jobs from markdown files using Vite's glob import
   * Returns jobs sorted by posted date (newest first)
   */
  export async function loadAllJobs(): Promise<Job[]> {
    // Use Vite's import.meta.glob to dynamically load all .md files from src/content/jobs/
    const globModules = import.meta.glob<string>('/src/content/jobs/*.md', { 
      query: '?raw', 
      import: 'default' 
    });
  
    const jobs: Job[] = [];
  
    // Load and parse each markdown file
    for (const [filePath, loader] of Object.entries(globModules)) {
      const markdown = await loader();
      const { frontmatter, content } = parseFrontmatter(markdown);
  
      // Extract filename as job ID (e.g., "01-frontend-developer" from "/src/content/jobs/01-frontend-developer.md")
      const filename = filePath.split('/').pop()?.replace('.md', '') || '';
  
      jobs.push({
        id: filename,
        title: frontmatter.title || '',
        company: frontmatter.company || '',
        location: frontmatter.location || '',
        salary: frontmatter.salary || '',
        posted: frontmatter.posted || '',
        deadline: frontmatter.deadline || '',
        type: frontmatter.type || '',
        experience: frontmatter.experience || '',
        tags: frontmatter.tags || '',
        body: content,
      });
    }
  
    // Sort by posted date (newest first)
    jobs.sort((a, b) => new Date(b.posted).getTime() - new Date(a.posted).getTime());
  
    return jobs;
  }
  
  /**
   * Load a single job by ID
   */
  export async function loadJobById(id: string): Promise<Job | null> {
    const jobs = await loadAllJobs();
    return jobs.find(job => job.id === id) || null;
  }
  
  /**
   * Search jobs by keyword (in title, company, location, or tags)
   */
  export async function searchJobs(query: string): Promise<Job[]> {
    const jobs = await loadAllJobs();
    const lowerQuery = query.toLowerCase();
    
    return jobs.filter(
      job =>
        job.title.toLowerCase().includes(lowerQuery) ||
        job.company.toLowerCase().includes(lowerQuery) ||
        job.location.toLowerCase().includes(lowerQuery) ||
        job.tags.toLowerCase().includes(lowerQuery)
    );
  }
  
  /**
   * Get unique tags from all jobs
   */
  export async function getAllTags(): Promise<string[]> {
    const jobs = await loadAllJobs();
    const tagsSet = new Set<string>();
    
    jobs.forEach(job => {
      job.tags.split(',').forEach(tag => {
        tagsSet.add(tag.trim());
      });
    });
    
    return Array.from(tagsSet).sort();
  }
  